import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage, scrollToTop } from "helpers/common";
import { toast } from "helpers/toast";
import { Button, DateInput, Input } from "components/ui";
import { formatDay, moduleOf, monthsBetween, timeOf, toDay } from "helpers/periods";
import { usePeriodStrings } from "translation/periodManagement";
import { useTranslation } from "translation/useTranslation";

export default function PeriodEditForm({
  period,
  periods,
  activePeriod,
  validateEvaluationPeriod,
  setError,
  refetchAll,
  onCancel,
  onSaved,
}) {
  const { pt, ps } = usePeriodStrings();
  const { translate } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [name, setName] = useState(period?.name ?? "");
  const [description, setDescription] = useState(period?.description ?? "");
  const [start, setStart] = useState(period?.evaluation_period_start_date ?? "");
  const [end, setEnd] = useState(period?.evaluation_period_end_date ?? "");

  /** Reopening the form on another period re-seeds it rather than remounting. */
  useEffect(() => {
    setName(period?.name ?? "");
    setDescription(period?.description ?? "");
    setStart(period?.evaluation_period_start_date ?? "");
    setEnd(period?.evaluation_period_end_date ?? "");
  }, [period]);

  /** The active period is the one the app is currently running on: its name is
      referenced elsewhere, so only its description and dates may change. */
  const isActivePeriod = period?.name === activePeriod;

  const nameEmpty = name.trim().length === 0;
  const nameTaken = useMemo(() => (periods || []).some((item) => (
    item.id !== period?.id && item.name?.trim().toLowerCase() === name.trim().toLowerCase()
  )), [periods, period, name]);

  /**
   * Create rejects a single day period, edit does not: existing periods with
   * equal start and end dates have to stay savable.
   */
  const endNotBeforeStart = timeOf(end) >= timeOf(start);
  const canSubmit = !nameEmpty && !nameTaken && endNotBeforeStart;

  let nameHint = pt("period_management.name.free");
  let nameHintKind = "ok";

  if (isActivePeriod) {
    nameHint = translate("period_management.cannot_modify_currently_active_period");
    nameHintKind = "";
  } else if (nameEmpty) {
    nameHint = pt("period_management.name.required");
    nameHintKind = "error";
  } else if (nameTaken) {
    nameHint = pt("period_management.name.taken");
    nameHintKind = "error";
  }

  const startYear = toDay(start)?.isValid() ? toDay(start).year() : "";

  let dateHint = endNotBeforeStart ? ps("period_management.date.months", { months: monthsBetween(start, end) }) : "";
  let dateHintKind = "";

  if (!endNotBeforeStart) {
    dateHint = ps("period_management.date.end_before_start");
    dateHintKind = "error";
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading) return;

    setError(null);

    const values = {
      name: name.trim(),
      description,
      evaluation_period_start_date: start,
      evaluation_period_end_date: end,
    };

    /**
     * Two periods of one module may never overlap. The check runs against the
     * whole module rather than against the immediate neighbours, so it stays a
     * submit time check reported at the top of the page.
     */
    const conflict = validateEvaluationPeriod(values, moduleOf(period), period?.id);
    if (conflict !== true) {
      let conflictRange = '';
      if (conflict?.evaluation_period_start_date) {
        conflictRange = `(${translate('period_management.evaluation_period')}: ${formatDay(conflict.evaluation_period_start_date)} - ${formatDay(conflict.evaluation_period_end_date)})`;
      }

      setError(translate('period_management.overlap_evaluation_period_error').replace('$X', `<br><b>${conflict.name}</b> ${conflictRange}`));
      scrollToTop();
      return;
    }

    setLoading(true);

    try {
      const { status } = await api.patch('/periods/' + period.id, values);
      if (status === 200) {
        refetchAll();
        onSaved();
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save period'));
      Sentry.captureException(err);
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="peb-period-form" data-testid="period-edit-form">
      <div className="peb-period-form__row">
        <div className="peb-period-form__col">
          <label className="peb-field__label" htmlFor="period-name">
            {pt("period_management.field.name")} <span className="peb-field__req">*</span>
          </label>
          <Input
            id="period-name"
            type="text"
            maxLength={255}
            error={!isActivePeriod && (nameEmpty || nameTaken)}
            value={name}
            readOnly={isActivePeriod}
            onChange={(e) => setName(e.target.value)} data-testid="period-name-input"
          />
          <div className={`peb-period-form__hint ${nameHintKind ? `peb-period-form__hint--${nameHintKind}` : ""}`}>
            {nameHint}
          </div>
        </div>

        <div className="peb-period-form__col">
          <label className="peb-field__label" htmlFor="period-description">
            {pt("period_management.field.description")}{" "}
            <span className="peb-period-form__hint">{ps("period_management.field.optional")}</span>
          </label>
          <Input
            id="period-description"
            type="text"
            maxLength={50}
            placeholder={ps("period_management.field.description_placeholder", { year: startYear })}
            value={description}
            onChange={(e) => setDescription(e.target.value)} data-testid="period-description-input"
          />
          <div className="peb-period-form__hint">{pt("period_management.field.description_hint")}</div>
        </div>

        <div className="peb-period-form__col">
          <label className="peb-field__label" htmlFor="period-start">
            {pt("period_management.detail.evaluation_period")} <span className="peb-field__req">*</span>
          </label>
          <div className="peb-period-form__dates">
            <DateInput
              id="period-start"
              value={start}
              valueFormat="YYYY-MM-DD"
              required
              onChange={(event) => setStart(event.target.value)}
              testId="period-start-input"
            />
            <span>-</span>
            <DateInput
              id="period-end"
              value={end}
              min={start}
              error={!endNotBeforeStart}
              valueFormat="YYYY-MM-DD"
              required
              onChange={(event) => setEnd(event.target.value)}
              testId="period-end-input"
            />
          </div>
          <div className={`peb-period-form__hint ${dateHintKind ? `peb-period-form__hint--${dateHintKind}` : ""}`}>
            {dateHint}
          </div>
        </div>
      </div>

      <div className="peb-period-form__actions">
        <Button variant="secondary" onClick={onCancel} data-testid="cancel-button">
          {ps("period_management.button.cancel")}
        </Button>
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={loading} data-testid="save-button">
          {ps("period_management.button.save")}
        </Button>
      </div>
    </form>
  );
}
